import React from "react";
import { FlightDetails } from "../types";
import { formatDateTime } from "utils/dateUtils";
import { getAirportOffsetLabel } from "utils/airportTimezones";
import DefaultAirLogo from "../../../assets/images/logo.svg";

interface FlightCardProps {
  flight: FlightDetails;
  isSelected: boolean;
  readOnly?: boolean;
  onSelect: (flight: FlightDetails) => void;
  onPostRequest?: (flight: FlightDetails) => void;
  posting?: boolean;
  posted?: boolean;
  isHighlighted?: boolean;
  highlightLabel?: string;
}

const statusColorFor = (flight: FlightDetails) => {
  const s = flight.liveStatus;
  if (s === "Landed" || s === "Arrived")
    return { bg: "bg-green-500", text: "text-white" };
  if (s === "Cancelled") return { bg: "bg-red-500", text: "text-white" };
  if (s === "Early") return { bg: "bg-blue-500", text: "text-white" };
  if (flight.delayMinutes && flight.delayMinutes > 0)
    return { bg: "bg-orange-500", text: "text-white" };
  return { bg: "bg-blue-500", text: "text-white" };
};

const FlightCard: React.FC<FlightCardProps> = ({
  flight,
  isSelected,
  readOnly = false,
  onSelect,
  onPostRequest,
  posting = false,
  posted = false,
  isHighlighted = false,
  highlightLabel,
}) => {
  const depTime =
    flight.actualDeparture || flight.estimatedDeparture || flight.departureTime;
  const arrTime = flight.estimatedArrival || flight.arrivalTime;
  const liveDurMin = flight.liveDurationMinutes;
  const durationText =
    liveDurMin != null && liveDurMin > 0
      ? `${Math.floor(liveDurMin / 60)}h ${liveDurMin % 60}m`
      : flight?.elptim
        ? `${parseInt(flight.elptim.slice(0, 3), 10)}h ${parseInt(flight.elptim.slice(3, 5), 10)}m`
        : "";
  const statusColors = statusColorFor(flight);

  const depOffset = getAirportOffsetLabel(flight.departureCode, depTime);
  const arrOffset = getAirportOffsetLabel(flight.arrivalCode, arrTime);

  return (
    <div
      className={`relative bg-white rounded-xl p-4 sm:p-5 border transition-all ${
        readOnly ? "" : "cursor-pointer"
      } ${
        isHighlighted
          ? "border-2 border-[#FFC82C] shadow-[0_0_20px_rgba(255,200,44,0.35)]"
          : !readOnly && isSelected
            ? "border-[#FFD700] shadow-lg ring-1 ring-[#FFD700]/30"
            : "border-gray-100 shadow-sm" + (readOnly ? "" : " hover:border-gray-200 hover:shadow-md")
      }`}
      onClick={() => {
        if (readOnly) return;
        onSelect(flight);
      }}
    >
      {/* Floating status pill at top-left */}
      {flight.liveStatus && (
        <span
          className={`absolute -top-2.5 left-4 inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-medium shadow-sm z-10 ${statusColors.bg} ${statusColors.text}`}
        >
          <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
          {flight.liveStatus}
          {flight.delayMinutes != null && flight.delayMinutes !== 0 && (
            <span className="ml-0.5">
              {flight.delayMinutes > 0
                ? `+${flight.delayMinutes}m`
                : `${flight.delayMinutes}m`}
            </span>
          )}
        </span>
      )}

      {/* Top Header Row: Airline Info + Post Request Button */}
      <div className="flex items-center justify-between gap-3 mb-3 pb-3 border-b border-gray-100">
        <div className="flex items-center gap-2.5 sm:gap-3 min-w-0 flex-1">
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full border border-gray-200 bg-white shrink-0 flex items-center justify-center overflow-hidden p-1">
          <img
            className="w-full h-full object-contain"
            src={flight.airlineLogo || DefaultAirLogo}
            alt={flight.airlineName}
            onError={(e) => {
              e.currentTarget.src = DefaultAirLogo;
            }}
            loading="lazy"
          />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-grease-100 text-sm sm:text-base font-semibold leading-tight truncate" title={flight.airlineName}>
              {flight.airlineName}
            </p>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-gray-500 text-xs font-medium truncate">
                {flight.flightNumber}
              </span>
              {(flight.terminal || flight.gate) && (
                <span className="text-gray-400 text-[10px] font-normal truncate">
                  {flight.terminal ? `T${flight.terminal}` : ""}{flight.terminal && flight.gate ? " · " : ""}{flight.gate ? `G${flight.gate}` : ""}
                </span>
              )}
            </div>
          </div>
        </div>

        {!readOnly && onPostRequest && (
          <button
            type="button"
            disabled={posting || posted}
            onClick={(e) => {
              e.stopPropagation();
              if (posted) return;
              onPostRequest(flight);
            }}
            className={`shrink-0 px-3.5 py-1.5 rounded-lg text-white text-xs font-semibold transition-colors flex items-center gap-1.5 shadow-sm ${
              posted
                ? "bg-green-600 cursor-default"
                : "bg-black hover:bg-neutral-800"
            } ${posting ? "opacity-70 cursor-not-allowed" : ""}`}
          >
            <span>{posted ? "Posted" : posting ? "Posting..." : "Post"}</span>
            {posted && (
              <svg
                className="w-3.5 h-3.5 text-white shrink-0"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 6L9 17L4 12" />
              </svg>
            )}
            {!posting && !posted && (
              <svg
                className="w-3.5 h-3.5 text-white shrink-0"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M22 2L11 13" />
                <path d="M22 2L15 22L11 13L2 9L22 2Z" />
              </svg>
            )}
          </button>
        )}
      </div>

      {/* Bottom Timeline Row: Departure - Flight Path - Arrival */}
      <div className="flex items-center justify-between gap-2 sm:gap-3">
        {/* Departure */}
        <div className="flex flex-col items-start min-w-[65px] sm:min-w-[75px] shrink-0">
          <div className="flex items-center gap-1">
            <i className="st-top-right text-gray-400 text-[8px]" />
            <p className="text-grease-100 text-sm sm:text-base font-semibold leading-tight">
              {formatDateTime(depTime, {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              })}
            </p>
          </div>
          {depOffset && (
            <p className="text-gray-400 text-[9px] sm:text-[10px] leading-tight whitespace-nowrap mt-0.5">
              {depOffset}
            </p>
          )}
          <p className="text-grease-100 text-base sm:text-lg font-bold mt-1 tracking-tight">
            {flight.departureCode}
          </p>
          <p className="text-gray-500 text-[10px] sm:text-[11px] mt-0.5 truncate max-w-[65px] sm:max-w-[75px]" title={flight.departureAirportName}>
            {flight.departureAirportName?.split(" ")[0]}
          </p>
        </div>

        {/* Flight Path */}
        <div className="flex flex-col items-center flex-1 min-w-[50px] px-1 sm:px-2">
          <div className="flex items-center w-full">
            <div className="w-2 h-2 rounded-full border-2 border-light-yellow shrink-0" />
            <div className="flex-1 border-b-2 border-dashed border-gray-200 mx-1 sm:mx-1.5" />
            <i className="st-flight-slim text-grease-100 text-xs sm:text-sm shrink-0" />
            <div className="flex-1 border-b-2 border-dashed border-gray-200 mx-1 sm:mx-1.5" />
            <div className="w-2 h-2 rounded-full border-2 border-light-yellow shrink-0" />
          </div>
          <p className="text-gray-500 text-[10px] sm:text-[11px] font-medium text-center mt-1.5 whitespace-nowrap">
            {durationText}
          </p>
          {flight?.stops > 0 && (
            <p className="text-orange-500 text-[10px] font-medium text-center mt-0.5 truncate max-w-full">
              {flight.stops} {flight.stops === 1 ? "Stop" : "Stops"}
              {flight.intermediateAirports &&
                flight.intermediateAirports.length > 0 && (
                  <span
                    title={flight.intermediateAirports
                      .map((a) => a.name || a.code)
                      .join(", ")}
                  >
                    {" · via "}
                    {flight.intermediateAirports
                      .map((a) => a.code)
                      .join(", ")}
                  </span>
                )}
            </p>
          )}
        </div>

        {/* Arrival */}
        <div className="flex flex-col items-end text-right min-w-[65px] sm:min-w-[75px] shrink-0">
          <div className="flex items-center justify-end gap-1">
            <i className="st-bottom-right text-gray-400 text-[8px]" />
            <p className="text-grease-100 text-sm sm:text-base font-semibold leading-tight">
              {formatDateTime(arrTime, {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              })}
              {flight.arrday && Number(flight.arrday) > 0 && (
                <span className="ml-0.5 text-[10px] font-medium text-red-500">
                  +{flight.arrday}
                </span>
              )}
            </p>
          </div>
          {arrOffset && (
            <p className="text-gray-400 text-[9px] sm:text-[10px] leading-tight whitespace-nowrap mt-0.5">
              {arrOffset}
            </p>
          )}
          <p className="text-grease-100 text-base sm:text-lg font-bold mt-1 tracking-tight">
            {flight.arrivalCode}
          </p>
          <p className="text-gray-500 text-[10px] sm:text-[11px] mt-0.5 truncate max-w-[65px] sm:max-w-[75px]" title={flight.arrivalAirportName}>
            {flight.arrivalAirportName?.split(" ")[0]}
          </p>
        </div>
      </div>
    </div>
  );
};

export default FlightCard;
